// Where the console's data comes from: mygaz-dev through the ops_* reads
// (migration 0043), or the demo set when the console runs as a design preview.
import { supabase } from './supabase';
import { demoLive, demoOrders, demoOverview } from './demo';
import { demoDevices, demoHome, demoHomes } from './demo-homes';
import { demoActions } from './demo-actions';
import { demoFinance, demoTeam } from './demo-money';
import { demoBrandView, demoMarketers } from './demo-brands';
import { demoAsk } from './demo-gazelle';
import { demoAgences, demoAgencesDay, demoCalmStatement, demoRider, demoStatement } from './demo-agences';
import { brandAssets } from './brands';
import type { AdminStatement, AgRider, Agences, AgencesDay, RiderFile, BrandView, CancelReason, GzReply, GzTurn, Devices, Finance, Marketers, TeamAgency, HomeDetail, HomeRow, JournalEntry, Live, Me, OpsOrder, Overview, PickRider } from './types';

export interface Source {
  demo: boolean;
  overview(): Promise<Overview>;
  orders(): Promise<OpsOrder[]>;
  live(): Promise<Live>;
  homes(): Promise<HomeRow[]>;
  home(id: string): Promise<HomeDetail>;
  devices(): Promise<Devices>;
  ridersFor(order: string): Promise<PickRider[]>;
  assign(order: string, rider: string, force?: boolean): Promise<OpsOrder>;
  cancel(order: string, reason: CancelReason, note?: string): Promise<OpsOrder>;
  remind(customers: string[]): Promise<{ sent: number; skipped: number }>;
  journal(): Promise<JournalEntry[]>;
  finance(days: 7 | 14 | 30): Promise<Finance>;
  team(): Promise<TeamAgency[]>;
  /** The Agences tab (D-59, 0051): every agency, one rider's file, the day, MyGaz's papers check. */
  agences(): Promise<Agences>;
  rider(id: string): Promise<RiderFile>;
  agencesDay(): Promise<AgencesDay>;
  checkPapers(rider: string, ok: boolean): Promise<AgRider>;
  /** « Voir le compte »: the agency's statement with the terms (0054), and an adjustment. */
  agencyStatement(agency: string): Promise<AdminStatement>;
  adjust(agency: string, amount: number, note: string): Promise<void>;
  marketers(): Promise<Marketers>;
  brandView(code: string): Promise<BrandView>;
  /** Gazelle: one answer to the conversation so far. Throws Error('no_key' | 'not_deployed' | …) when she cannot answer. */
  ask(turns: GzTurn[], tab?: string): Promise<GzReply>;
  /** Calls back on any change to orders or rider positions; returns the unsubscribe. */
  subscribe(onChange: () => void): () => void;
}

async function rpc<T>(fn: string, args?: Record<string, unknown>): Promise<T> {
  if (!supabase) throw new Error('no_backend');
  const { data, error } = await supabase.rpc(fn, args);
  if (error) throw error;
  return data as T;
}

// The gazelle Edge Function, with the admin's session. Its error code (no_key,
// not_admin, model_error) comes back as the Error's message; a function that
// is not deployed yet answers 404.
async function askGazelle(turns: GzTurn[], tab?: string): Promise<GzReply> {
  if (!supabase) throw new Error('no_backend');
  const { data, error } = await supabase.functions.invoke<GzReply>('gazelle', { body: { messages: turns, tab } });
  if (!error && data) return data;
  const res = (error as { context?: Response } | null)?.context;
  if (res instanceof Response) {
    if (res.status === 404) throw new Error('not_deployed');
    const body = await res.json().catch(() => null) as { error?: string } | null;
    throw new Error(body?.error ?? 'http_' + res.status);
  }
  throw new Error(error?.message ?? 'no_answer');
}

export const liveSource: Source = {
  demo: false,
  overview: () => rpc<Overview>('ops_overview'),
  orders: () => rpc<OpsOrder[]>('ops_orders'),
  live: async () => {
    const [live, zones, map] = await Promise.all([
      rpc<Live>('ops_live'),
      supabase ? supabase.from('pilot_zones').select('quartier').eq('active', true) : Promise.resolve({ data: null }),
      // The map's homes and stock (0050); the map still works without them.
      rpc<Live['map']>('ops_map').catch(() => undefined),
    ]);
    return { ...live, zones: (zones.data ?? []).map((z: { quartier: string }) => z.quartier), map };
  },
  homes: () => rpc<HomeRow[]>('ops_homes'),
  home: (id) => rpc<HomeDetail>('ops_home', { p_customer: id }),
  devices: () => rpc<Devices>('ops_devices'),
  ridersFor: (order) => rpc<PickRider[]>('ops_riders_for', { p_order: order }),
  assign: (order, rider, force = false) => rpc<OpsOrder>('ops_assign', { p_order: order, p_rider: rider, p_force: force }),
  cancel: (order, reason, note) => rpc<OpsOrder>('ops_cancel', { p_order: order, p_reason: reason, p_note: note ?? null }),
  remind: (customers) => rpc<{ sent: number; skipped: number }>('ops_remind', { p_customers: customers }),
  journal: () => rpc<JournalEntry[]>('ops_journal_list'),
  finance: (days) => rpc<Finance>('ops_finance', { p_days: days }),
  team: () => rpc<TeamAgency[]>('ops_team'),
  agences: () => rpc<Agences>('ops_agences'),
  rider: (id) => rpc<RiderFile>('ops_rider', { p_rider: id }),
  agencesDay: () => rpc<AgencesDay>('ops_agences_day'),
  checkPapers: (rider, ok) => rpc<AgRider>('ops_check_papers', { p_rider: rider, p_ok: ok }),
  agencyStatement: (agency) => rpc<AdminStatement>('ops_agency_statement', { p_agency: agency, p_days: 14 }),
  adjust: async (agency, amount, note) => { await rpc('ops_adjust', { p_agency: agency, p_amount: amount, p_note: note }); },
  marketers: async () => {
    const [mk, assets] = await Promise.all([rpc<Marketers>('ops_marketers'), brandAssets()]);
    return { ...mk, brands: mk.brands.map((b) => ({ ...b, ...assets[`${b.country}/${b.code}`] })) };
  },
  brandView: async (code) => {
    const [v, assets] = await Promise.all([rpc<BrandView>('ops_brand_view', { p_code: code }), brandAssets()]);
    return { ...v, ...assets[`CM/${v.code}`] };
  },
  ask: askGazelle,
  subscribe(onChange) {
    if (!supabase) return () => {};
    const client = supabase;
    let t: ReturnType<typeof setTimeout> | undefined;
    // Several rows change together (an order and its event): one refresh for the burst.
    const soon = () => { clearTimeout(t); t = setTimeout(onChange, 400); };
    const ch = client.channel('ops-console')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, soon)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'order_positions' }, soon)
      .subscribe();
    return () => { clearTimeout(t); client.removeChannel(ch); };
  },
};

export const demoSource: Source = {
  demo: true,
  overview: async () => demoActions.overview(demoOverview()),
  orders: async () => demoActions.apply(demoOrders()),
  live: async () => demoLive(),
  homes: async () => demoHomes(),
  home: async (id) => demoHome(id),
  devices: async () => demoDevices(),
  ridersFor: async (order) => demoActions.riders(order),
  assign: async (order, rider) => demoActions.assign(order, rider),
  cancel: async (order, reason, note) => demoActions.cancel(order, reason, note),
  remind: async (customers) => demoActions.remind(customers),
  journal: async () => demoActions.journal(),
  finance: async (days) => demoFinance(days),
  team: async () => demoTeam(),
  agences: async () => demoAgences(),
  rider: async (id) => demoRider(id),
  agencesDay: async () => demoAgencesDay(),
  checkPapers: async (rider, ok) => ({ ...demoRider(rider), papers_checked_at: ok ? new Date().toISOString() : null }),
  agencyStatement: async (agency) => {
    const a = demoAgences().agencies.find((x) => x.agency_id === agency) ?? demoAgences().agencies[0];
    return { ...(a.agency_id === 'demo-ag-calm' ? demoCalmStatement() : demoStatement(a.balance)), account: a, topup_before: new Date(Date.now() + (a.days_to_pause ?? 3) * 86400000).toISOString() };
  },
  adjust: async () => {},
  marketers: async () => demoMarketers(),
  brandView: async (code) => demoBrandView(code),
  ask: async (turns) => demoAsk(turns),
  subscribe: (onChange) => demoActions.listen(onChange),
};

export async function fetchMe(): Promise<Me | null> {
  return rpc<Me | null>('ops_me');
}
